import { Container, Text, Box, Flex } from '@chakra-ui/react'
import { Hero, Layout, StickyHeader, WebPerformanceDetailHeroCard } from '@common'
import { ISSRPageData } from '@interfaces'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { Link as SSMLink } from '@ui'
import { Link as LinkIcon } from '@icon'
import { ShowMore } from '@common'
import { Markup } from 'interweave'
import { useState, useEffect } from 'react'
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Input, Textarea, Button, useBreakpointValue, useToast } from '@chakra-ui/react'
import { WebPerformanceFormService } from '@/src/services'

const COOLDOWN_SECONDS = 30

const schema = yup.object().shape({
  webperformance_age: yup
    .mixed()
    .transform((v) => {
      if (v === '' || v === undefined || v === null || (typeof v === 'number' && Number.isNaN(v))) return undefined
      return Number(v)
    })
    .test('type', 'Yaş sayı olmalıdır', (v) => v === undefined || (typeof v === 'number' && !Number.isNaN(v)))
    .required('Yaş zorunludur')
    .test('positive', 'Yaş sayı olmalıdır', (v) => v === undefined || (typeof v === 'number' && v > 0))
    .test('integer', 'Yaş sayı olmalıdır', (v) => v === undefined || (typeof v === 'number' && Number.isInteger(v))),
  webperformance_question: yup.string().required('Bu alan zorunludur'),
})

// Positions, sizes, and weights for the animated questions (text will come from i18n)
const questionPositions = [
  { top: '7%', left: { base: '95%', lg: '50%' }, size: '32px', weight: 'bold' },
  { top:  { base: '14%', lg: '16%' }, right:  { base: '15%', lg: '7%' }, size: '28px', weight: 'bold' },
  { top: '20%', left: { base: '65%', lg: '50%' }, size: '26px', weight: '300' },
  { top: '27%', right: { base: '15%', lg: '30%' }, size: '28px', weight: 'bold' },
  { top: '32%', left:  { base: '75%', lg: '36%' }, size: '26px', weight: '300' },
  { top:  { base: '38%', lg: '36%' }, right:  { base: '35%', lg: '20%' }, size: '24px', weight: '300' },
]


const TypewriterText = ({ text, delay, size, weight, inView }) => {
  const [displayed, setDisplayed] = useState('')

  useEffect(() => {
    if (!inView) return

    let i = 0
    const start = setTimeout(() => {
      const interval = setInterval(() => {
        setDisplayed(text.slice(0, i + 1))
        i++
        if (i >= text.length) clearInterval(interval)
      }, 40)

      return () => clearInterval(interval)
    }, delay * 1000)

    return () => clearTimeout(start)
  }, [inView, text, delay])

  return (
    <span
      style={{
        position: 'absolute',
        right: '100%',
        top: 0,
        whiteSpace: 'nowrap',
        fontFamily: 'mikado, sans-serif',
        fontSize: size,
        fontWeight: weight,
      }}
    >
      {displayed}
    </span>
  )
}

const AnimatedQuestionCloud = () => {

  const isLg = useBreakpointValue({ base: false, lg: true })
  const r = (v) => (v && typeof v === 'object' ? (isLg ? v.lg : v.base) : v)

  const { t } = useTranslation()
  const toast = useToast()
  const { locale } = useRouter()
  const controls = useAnimation()
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  })
  const [cooldownRemaining, setCooldownRemaining] = useState(0)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ webperformance_age?: number; webperformance_question: string }>({
    resolver: yupResolver(schema),
    defaultValues: { webperformance_question: '' },
  })

  useEffect(() => {
    if (cooldownRemaining <= 0) return
    const timer = setInterval(() => {
      setCooldownRemaining((s) => (s <= 1 ? 0 : s - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [cooldownRemaining])

  const cooldownDisplay = cooldownRemaining > 0
    ? `${Math.floor(cooldownRemaining / 60)}:${String(cooldownRemaining % 60).padStart(2, '0')}`
    : ''

  // Localized questions text
  const cloud = (t(
    'webperformance:cloudQuestions',
    {},
    { returnObjects: true }
  ) as unknown) as string[]
  const questions = questionPositions.map((pos, i) => ({
    ...pos,
    text: cloud?.[i] || '',
  }))

  const onSubmit = async (data) => {
    try {
      const response = await WebPerformanceFormService.post({
        webform_id: 'webperformance',
        webperformance_age: data.webperformance_age,
        webperformance_question: data.webperformance_question,
        locale,
      })
      if (response) {
        toast({
          title: t('webperformance:successTitle'),
          description: t('webperformance:successDescription'),
          status: 'success',
          duration: 4000,
          isClosable: true,
        })
        reset()
        setCooldownRemaining(COOLDOWN_SECONDS)
      }
    } catch (error) {
      toast({
        title: t('form:error.commonTitle'),
        description: t('form:error.commonDescription'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    }
  }
  useEffect(() => {
    if (inView) {
      controls.start((i) => ({
        opacity: 1,
        scale: 1,
        transition: {
          delay: i * 0.4,
          duration: 0.6,
          type: 'spring',
          stiffness: 120,
          damping: 12,
        },
      }))
    }
  }, [inView, controls])

  return (
    <Box
      ref={ref}
      bg="#F4D21F"
      position="relative"
      py="32"
      minH="650px"
      overflow="hidden"
    >
      {questions.map((item, index) => (
        <motion.div
          key={index}
          custom={index}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={controls}
          style={{
            position: 'absolute',
            top: r(item.top),
            left: r(item.left),
            right: r(item.right),
            fontSize: r({ base: '18px', lg: item.size }),
          }}
        >
          <Box position="relative" display="inline-block">

            <TypewriterText
              text={item.text}
              delay={index * 0.4 + 0.3}
              size={{ base: '15px', lg: item.size }}
              weight={item.weight}
              inView={inView}
            />

            <motion.span
              initial={{ scale: 0, rotate: -15 }}
              animate={inView ? { scale: [0, 1.2, 1], rotate: 0 } : {}}
              transition={{
                delay: index * 0.4,
                duration: 0.5,
                ease: 'easeOut',
              }}
              style={{
                fontSize: r({ base: '18px', lg: item.size }),
                fontWeight: item.weight,
                display: 'inline-block',
              }}
            >
              ?
            </motion.span>

          </Box>
        </motion.div>
      ))}
      {/* FORM */}
      <Box
        position="absolute"
        top="45%"
        left="50%"
        transform="translateX(-50%)"
        width="90%"
        maxW="860px"
      >
        <Box as="form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="webperformance_age"
            render={(props: { field?: { value?: number; onChange: (v: number | undefined) => void }; value?: number; onChange?: (v: number | undefined) => void }) => {
              const value = props.field?.value ?? props.value
              const onChange = props.field?.onChange ?? props.onChange
              return (
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={value ?? ''}
                  onChange={(e) => {
                    const v = e.target.value
                    onChange?.(v === '' ? undefined : Number(v))
                  }}
                  placeholder={t('webperformance:agePlaceholder')}
                  bg="white"
                  border="none"
                  borderRadius="0"
                  variant="secondary"
                  mb="2"
                  height="50px"
                  fontSize="sm"
                  fontWeight="bold"
                  _placeholder={{ color: 'black', fontWeight: 'bold' }}
                  _focus={{ outline: 'none', boxShadow: 'none' }}
                />
              )
            }}
          />
          {errors.webperformance_age && (
            <Text fontSize="xs" color="red.500" mb="1">{errors.webperformance_age.message}</Text>
          )}

          <Controller
            control={control}
            name="webperformance_question"
            render={(props: { field?: { value?: string; onChange: (v: string) => void }; value?: string; onChange?: (v: string) => void }) => {
              const value = props.field?.value ?? props.value
              const onChange = props.field?.onChange ?? props.onChange
              return (
                <Textarea
                  value={value ?? ''}
                  onChange={(e) => onChange?.(e.target.value)}
                  placeholder={t('webperformance:questionPlaceholder')}
                  bg="white"
                  border="none"
                  borderRadius="0"
                  mb="2"
                  height="150px"
                  fontSize="sm"
                  fontWeight="bold"
                  _placeholder={{ color: 'black', fontWeight: 'bold' }}
                  _focus={{ outline: 'none', boxShadow: 'none' }}
                  resize="none"
                />
              )
            }}
          />
          {errors.webperformance_question && (
            <Text fontSize="xs" color="red.500" mb="1">{errors.webperformance_question.message}</Text>
          )}

          <Flex justifyContent="flex-end" direction="column" align="flex-end" gap="2">
            {cooldownRemaining > 0 && (
              <Text fontSize="sm" color="red.700" fontWeight="medium">
                {t('webperformance:cooldownMessage', { time: cooldownDisplay })}
              </Text>
            )}
            <Button
              type="submit"
              bg="#8B1A1A"
              color="white"
              borderRadius="0"
              px="10"
              py="6"
              fontSize="sm"
              fontWeight="medium"
              _hover={{ bg: cooldownRemaining > 0 ? '#8B1A1A' : '#6e1414' }}
              isDisabled={cooldownRemaining > 0}
              opacity={cooldownRemaining > 0 ? 0.7 : 1}
            >
              {t('webperformance:submitLabel')}
            </Button>
          </Flex>
        </Box>
      </Box>
    </Box>
  )
}
const SuzanneLacyBirliktePage = ({ pageData, global: { navigation } }: ISSRPageData) => {
  const { t } = useTranslation()
  const { asPath, locale } = useRouter()
  const [activeTab, setActiveTab] = useState(0)

  const hero = pageData?.data?.hero || {
    subTitle: t('webperformance:heroSubTitle'),
    title: t('webperformance:heroTitle'),
    description: t('webperformance:heroDescription'),
  }

  const menuData = [
    { title: t('webperformance:artistText') },
    { title: t('webperformance:curatorialText') },
    { title: t('webperformance:aboutWebPerformance') },
    { title: t('webperformance:exhibitionCatalog'), href: 'https://drupal.sakipsabancimuzesi.org/sites/default/files/2026-03/SL_Katalog%20Web.pdf' },
  ]

  const teamData = [
    {
      names: [
        { name: 'Ahu Antmen', title: locale === 'en' ? 'Museum Manager' : 'Müze Müdürü' },
        { name: 'Hüma Arslaner', title: locale === 'en' ? 'Exhibition Management' : 'Sergi Yönetimi' },
        { name: 'Osman Serhat Karaman', title: locale === 'en' ? 'Digital Museum Practices' : 'Dijital Müze Uygulamaları' },
        { name: 'Gözde Oral', title: 'Birebir Tasarım Çözümleri' },
        { name: 'Emrah Yıldız', title: locale === 'en' ? 'Software Specialist' : 'Yazılım Uzmanı' },
        { name: 'Aslıhan Akın', title: locale === 'en' ? 'Events' : 'Etkinlikler' },
        { name: 'İrem Biçer Aslan', title: locale === 'en' ? 'digitalSSM Archive & Research' : 'digitalSSM Arşiv ve Araştırma' },
      ],
      role: t('webperformance:projectTeamRole')
    },
  ]

  const sectionData = [
    {
      stickyTitle:
        locale === 'en'
          ? '“I’ve used questions as content, as an organizing device, and as a way to align participants’ values and build a sense of ownership.”'
          : '“Ben de soruları hem içerik oluşturmak, hem örgütleyici bir  mekanizma kurmak, hem de katılımcıların değerlerini yan yana getirerek  sahiplenme duygusu yaratan bir araç olarak kullandım.”',
      staticDescriptions:
        locale === 'en'
          ? '<b>A Question-Led Practice</b>' +
            '<p></br>Social practice is question-led, suggesting that the artist is curious, asks questions about social issues, and listens carefully to the variety of answers she receives. In general, there is an encompassing question "What do we <strong>not</strong> know?" Or "How can society do better?" Feminists and political artists have long used this practice to engage a broader public in their work.</p>' +
            "<p></br>I've used questions as content, as an organizing device, and as a way to align participants' values and build a sense of ownership. This can be a simple question; like, \"If you've ever been in a situation of domestic violence, do you remember that specific moment when you tried to ask for help and couldn't?\" In my work <em>The Tattooed Skeleton</em> 400 women across Spain responded to this question. Their responses were later hand-written on to 400 masks.</p>" +
            '<p></br>In <em>Between the Door and the Street,</em> we used the process of collectively drafting questions in order to formulate a "territory for conversation" that could serve as a "script" for participants. Over sixty small groups of women suggested questions they would like to discuss, which were presented to a board of advisors who grouped the questions and consolidated them. These were sent back to the small groups to review back and forth until an agreed upon series of questions could be selectively addressed during each groups\' participation in the whole performance.</p>' +
            "<p></br>When these collectively created questions were presented as installations and as a film at the Sakıp Sabancı Museum in Istanbul, the curator Ahu Antmen suggested we create a place for visitors to leave their own questions. To my surprise, over 1500 questions were left during the exhibition. I'm excited that the Sakıp Sabancı Museum will be continuing this project online as a way to spark conversation around issues raised in that project, and even more important, those questions that are specific to women in Turkey.</p>"
          : '<b>Soru Temelli Bir Pratik</b>' +
            '<p></br>Toplumsal pratik soru odaklıdır. Meraklı sanatçı, toplumsal meselelere dair sorular sorar ve gelen farklı karşılıkları dikkatle dinler. Çoğu durumda, "Neyi bilmiyoruz?" ya da "Toplum nasıl daha iyi olabilir?" gibi kapsayıcı bir sorudan yola çıkılır. Feminist sanatçılar ve politik sanatçılar, daha geniş toplulukları çalışmalarına katabilmek için bu pratiği uzun zamandır kullanıyor.</p>' +
            '<p></br>Ben de soruları hem içerik oluşturmak, hem örgütleyici bir mekanizma kurmak, hem de katılımcıların değerlerini yan yana getirerek sahiplenme duygusu yaratan bir araç olarak kullandım. Bazen çok basit bir soru sorarsınız: "Aile içi şiddetin yaşandığı bir durumda, yardım istemeye yeltendiğiniz halde, bunu yapamadığınız bir an hatırlıyor musunuz?" gibi. Örneğin <i>Dövmeli İskelet</i> adlı çalışmamda, İspanya\'nın dört bir yanından 400 kadın bu soruya yanıt verdi. Sonrasında bu yanıtlar 400 maskenin üzerine tek tek elle yazıldı.</p>' +
            '<p></br><i>Kapıyla Sokak Arasında</i> adlı çalışmada, soruları ortaklaşa üretme sürecini, katılımcılar için bir "konuşma alanı" yaratmak üzere kullandık. Bu alan, katılımcılar için bir "yönerge" gibi işledi. Altmıştan fazla küçük ölçekli kadın grubu, tartışmak istedikleri soruları önerdi, sonra bu sorular bir danışmanlar kuruluna sunuldu ve kurul da soruları tematik olarak kümelendirip tek bir liste haline getirdi. Bu liste yeniden küçük gruplara gönderildi. Karşılıklı okumalarla ilerleyen bir gözden geçirme trafiğinin sonunda, üzerinde uzlaşılan bir soru dizisi ortaya çıktı. Her grup, performansa katılımında bu diziden seçtiği soruları ele aldı.</p>' +
            "<p></br>Bu soru dizisi Türkiye'de, İstanbul'daki Sakıp Sabancı Müzesi'nde bir enstalasyon ve film olarak izleyiciyle buluştuğunda, küratör Ahu Antmen ziyaretçilerin kendi sorularını da bırakabilecekleri bir alan açmayı önerdi. Sonra hiç beklemediğim bir şey oldu; sergi boyunca 1500'ün üzerinde soru birikti. Müzenin bu girişimi çevrim içi ortamda sürdürecek olması beni çok heyecanlandırıyor. Böylece hem bu çalışmanın açtığı meseleler etrafında yeni konuşmalar filizlenecek hem de bugün Türkiye'deki kadınlara özgü sorulara daha görünür bir yer açılacak.</p>",
    },
    {
      stickyTitle:
        locale === 'en'
          ? '“Experimenting with the aesthetic possibilities of overcoming the conventional boundaries which define what art can be and\n' +
            "where and how art can be, Lacy's work has always been an encouragement to positive change in a world\n" +
            'with societal problems of collective existence.\n”'
          : "“Lacy'nin sanatın ne olabileceğini ve nerede, nasıl var olabileceğini belirleyen yerleşik sınırları aşmanın estetik olanaklarını kurcalayan yapıtları, kolektif varoluşun toplumsal gerilimleriyle örülü bir dünyada her zaman yapıcı dönüşüm fikrini teşvik etti.”",
      staticDescriptions:
        locale === 'en'
          ? '<b>Togæther: to gather, together\n</b>' +
            "<p></br>Suzanne Lacy's work fosters a gathering of people and information; it's an alternative organization of body politics and happens as a part of everyday life, both in and outside the art system. Like many artists of her generation who've taken a feminist and activist stance, she oversteps the conventional boundaries between art and life; between art and politics; and between aesthetics and ethics. In the 1970s, it was a rethinking of art's meaning and scope that defined her practice through the medium of the body – be it performance, photography or video. As her work transformed from individual performance to social practice in the 1980s, a reconsideration of what makes public art \"public\" enhanced her praxis set in a wide variety of public spaces, including museums. Experimenting with the aesthetic possibilities of overcoming the conventional boundaries which define what art can be and <em>where and how art can be</em>, her work has always been an encouragement to positive change in a world with societal problems of collective existence.</p>" +
            "<p></br>This web performance is borne out of and carries the same title as Suzanne Lacy's exhibition <em>Birlikte: Togæther</em> at the Sakıp Sabancı Museum in Istanbul during 12.09.2025–08.03.2026. Thinking for a title of the exhibition, a play on the concepts of getting together and gathering information seemed like a good conceptual entry point into the meaning of Suzanne's practice. Several of the video installations in the exhibition showed how her social practice always revolves around the asking of questions to open up discussion among participants. This not only serves as a metaphor of the fact that it is through questioning that new thoughts are provoked and any change can potentially happen, but is like the <em>happenings</em> of our new century, constituting a fundamental challenge to norms of conventional exhibition-making and museology.</p>" +
            "<p></br>Building on Suzanne Lacy's question-led practice, my concern as curator of the <em>Birlikte: Togæther</em> exhibition was how the museum as a public space could become a sounding board for local voices within an exhibition touching on pressing issues of gender injustice. We organized a question-sheet in which we asked visitors to write their own questions, and the only personal information we wanted them to provide was their age, to better understand generational concerns.</p>" +
            '<p></br>In the six months that the exhibition remained open, we collected questions visitors wrote on paper and left in a box. A selection of those questions were printed in the national weekly Gazete Oksijen on the 6th of March, 2026, hence carried into the public space of a newspaper page. And today, on the 8th of March 2026, <em>Birlikte: Togæther</em> becomes a self-generated web performance continuing to gather questions, this time in a digital public space, from all those who want to come together in the collectivity of our mutual concerns to build a more just culture of gender.</p>' +
            '<p></br><strong>Ahu Antmen</strong></p>'
          : '<b>Togæther: toplamak, birlikte</b>' +
            "<p></br>Suzanne Lacy'nin üretimi, insanları ve bilgiyi bir araya getiren bir buluşma zemini kurar; beden politikalarını alternatif bir örgütlenme biçimiyle yeniden düşünmeye çağırır ve gündelik hayatın bir parçası olarak, sanat sisteminin hem içinde hem de dışında gerçekleşir. Kendi kuşağından feminist ve aktivist bir duruş benimseyen pek çok sanatçı gibi, sanatla hayatın, sanatla politikanın, estetikle etiğin arasındaki yerleşik sınırları aşar. 1970'lerde pratiğini belirleyen, sanatın tanımını yeniden düşünmesiydi; bu yaklaşım, performans, fotoğraf ya da video olsun fark etmeksizin, bedeni bir mecra olarak kullanması üzerinden biçimlendi. 1980'lerde çalışmaları bireysel performanstan toplumsal pratiğe doğru evrilirken, kamusal sanatın \"kamusal\" olmasını sağlayan koşulları yeniden düşünmesi, müzeler dahil pek çok kamusal mekâna yayılan eylem pratiğini daha da zenginleştirdi. Sanatın ne olabileceğini ve nerede, nasıl var olabileceğini belirleyen yerleşik sınırları aşmanın estetik olanaklarını kurcalayan yapıtları, kolektif varoluşun toplumsal gerilimleriyle örülü bir dünyada her zaman yapıcı dönüşüm fikrini teşvik etti.</p>" +
            '<p></br>12.09.2025–08.03.2026 tarihleri arasında İstanbul\'daki Sakıp Sabancı Müzesi\'nde gerçekleşen <em>Birlikte: Togæther</em> başlıklı Suzanne Lacy sergisinden yola çıkan bu web performansı, sergiyle aynı adı taşıyor. Sergiye bir başlık ararken, "bir araya gelme" ve "bilgiyi toplama ve derleme" fikirlerini buluşturan bu kelime oyunu, Suzanne\'ın pratiğini kavramak için iyi bir kavramsal çözüm gibi göründü. Sergide gösterdiğimiz bazı video yerleştirmeleri, toplumsal pratiğinin daima soru sorma eylemi etrafında şekillendiğini açıkça gösteriyor. Bu sorular, katılımcılar arasında tartışma yaratmayı amaçlıyor. Bu, sorgulamanın yeni fikirler doğurduğuna ve değişimin kapısını aralayabildiğine dair bir metafor olmanın ötesinde; bu yüzyıla özgü <em>happening</em>\'ler gibi, alışıldık sergileme kalıplarına ve müzeciliğin yerleşik normlarına temel bir itiraz da getiriyor.</p>' +
            "<p></br>Dolayısıyla Suzanne Lacy'nin soru odaklı pratiğinden hareketle, <em>Birlikte: Togæther</em> sergisinin küratörü olarak benim için asıl mesele şuydu: Toplumsal cinsiyet adaletsizliği gibi hayati meselelere temas eden bir sergide, müze bir kamusal mekân olarak yerel kamunun sesi için nasıl bir yankılama alanına dönüşebilir? Buna cevaben, ziyaretçilerin kendi sorularını yazabilecekleri bir soru formu hazırladık. Kuşaklara özgü kaygıları okuyabilmek için, formda kişisel olarak sadece yaş bilgisinin belirtilmesini rica ettik.</p>" +
            "<p></br>Serginin açık kaldığı altı ay boyunca, ziyaretçilerin gönüllü olarak bıraktığı soruları yerleştirdiğimiz kutularda biriktirdik. 6 Mart 2026'da sorulardan oluşan bir seçki, yerel gazete Gazete Oksijen'in kamusal alanına taşındı. Ve bugün, 8 Mart 2026'da <em>Birlikte: Togæther</em> bu kez de dijital bir kamusal alanda kendi kendini üreten bir web performansına dönüşüyor. Toplumsal cinsiyet bakımından daha adil bir kültürü birlikte kurabilmek için, ortak dertlerimizin etrafında yan yana gelmek isteyen herkesten sorular toplamaya devam ediyor.</p>" +
            '<p></br><strong>Ahu Antmen</strong></p>',
    },
    {
      stickyTitle:
        locale === 'tr'
        ? 'Dijital ortam, performansın belgesi olmakla yetinmez; onun yeni bir tezahür alanına dönüşür. Sorular yeniden sıralanır, ilişkilendirilir, görselleştirilir ve yeni karşılaşmalara açılır. Böylece performans, hem gerçekleştiği ana bağlı kalır hem de o anın ötesine geçer.'
        : 'The digital environment does not merely function as a document of the performance; it becomes a new site of its manifestation. Questions are reordered, related to one another, visualized, and opened to new encounters. Thus the performance remains connected to the moment in which it occurred while also extending beyond it.',
      staticDescriptions:
        locale === 'en'
          ? '<p>The project unfolds through a two-phase structure. The first phase aims to allow the questions collected in the gallery space to continue living in the web environment. During the exhibition, the questions left by visitors move beyond the limits of the physical space and are carried into a digital field of encounter. In this way, the question ceases to be a record of a singular moment; it becomes part of a circulation that extends across different times and contexts.</p>' +
          '<p></br>The digital environment does not merely function as a document of the performance; it becomes a new site of its manifestation. Questions are reordered, related to one another, visualized, and opened to new encounters. Thus the performance remains connected to the moment in which it occurred while also extending beyond it.</p>' +
          '<p></br>The second phase of the project opens this transition to investigation. It examines how the performance continues to exist after its realization, and under which technical and institutional conditions it is reconstituted. This research is made visible through information design.</p>' +
          '<p></br>Here, information design is not treated as a representational tool but as a field that reveals the permeability of the boundary between performance and documentation. The web environment produces an intermediary form that is both performance and document. Rather than fixing the work in place, this intermediary form allows it to continue by changing over time.</p>' +
          '<p></br>In this way, the project both sustains the circulation of questions and invites reflection on the structural conditions that make this circulation possible.</p>'
          : '<p>İki aşamalı bir yapı üzerinden ilerleyen "Web Performans" projesinin ilk aşaması, galeri mekânında toplanan soruların web ortamında yaşamaya devam etmesini amaçlar. Sergi süresince ziyaretçilerin bıraktığı sorular, fiziksel mekânın sınırlarını aşarak dijital bir karşılaşma alanına taşınır. Böylece soru, tekil bir anın kaydı olmaktan çıkarak; farklı zamanlara ve bağlamlara yayılan bir dolaşımın parçası hâline gelir.</p>' +
          '<p></br>Dijital ortam, performansın belgesi olmakla yetinmez; onun yeni bir tezahür alanına dönüşür. Sorular yeniden sıralanır, ilişkilendirilir, görselleştirilir ve yeni karşılaşmalara açılır. Böylece performans, hem gerçekleştiği ana bağlı kalır hem de o anın ötesine geçer.</p>' +
          '<p></br>Projenin ikinci aşaması, bu geçişi araştırmaya açar. Performansın gerçekleşmesinin ardından nasıl varlık gösterdiği; hangi teknik ve kurumsal koşullar altında yeniden kurulduğu incelenir. Bu araştırma, bilgi tasarımı aracılığıyla görünür kılınır.</p>' +
          '<p></br>Bilgi tasarımı burada temsil edici bir araç değil; performans ile belgeleme arasındaki sınırın geçirgenliğini ortaya koyan bir alan olarak ele alınır. Web ortamı, yarı performans yarı belge niteliği taşıyan bir ara form üretir. Bu ara form, eserin sabitlenmesini değil; zaman içinde değişerek sürmesini mümkün kılar.</p>' +
          '<p></br>Proje böylece hem soruların dolaşımını sürdürür hem de bu dolaşımın yapısal koşullarını düşünmeye davet eder.</p>',
      artists: [],
    },
  ]

  return (
    <Layout
      title={hero?.title || t('common:ssmPageTitle')}
      navigation={navigation}
      path={asPath}
    >
      <Hero
        bgImageUrl="https://drupal.sakipsabancimuzesi.org/sites/default/files/2026-03/CAN09638-%20CANBERK%20-%20%40canberkulusan.jpg"
        mobileBgImageUrl="https://drupal.sakipsabancimuzesi.org/sites/default/files/2026-03/SL-8Mart_Web375_598_OK.jpg"
      >
        <WebPerformanceDetailHeroCard
          title={hero.title}
          displayBuyTicketButtonGroup={false}
          displayFooterButtonGroup={true}
        />
      </Hero>

      <Container
        maxW={{ base: 'container.lg', lg: 'container.xl' }}
        mt={{ base: '28', lg: '20' }}
        p={0}
      >
        {/* TAB HEADER */}
        <StickyHeader navs={[]} disableSticky>
          {menuData.map((item, index) => {
            if (index < 3) {
              return (
                <Box
                  key={index}
                  onClick={() => setActiveTab(index)}
                  fontSize="sm"
                  fontWeight={activeTab === index ? 'bold' : 'light'}
                  color="black.primary3"
                  cursor="pointer"
                  position="relative"
                  whiteSpace="pre"
                  _after={
                    activeTab === index
                      ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: '-4px',
                        height: '2px',
                        bg: 'red.primary',
                      }
                      : {}
                  }
                >
                  {item.title}
                </Box>
              )
            }

            return (
              <SSMLink
                key={index}
                display="flex"
                alignItems="center"
                fontSize="sm"
                fontWeight="light"
                color="black.primary3"
                position="relative"
                whiteSpace="pre"
                href={item.href}
                target="_blank"
              >
                {item.title}
                <LinkIcon ml="10px" width="15px" height="15px" />
              </SSMLink>
            )
          })}
        </StickyHeader>
      </Container>


      {/* TAB CONTENT */}
      <Box
        id={`tab-content-${activeTab}`}
        as="section"
        pt={{ base: '4', lg: '20' }}
        pb="padding.primary2"
      >
        <Container maxW={{ base: 'full', lg: 'container.secondary' }} mt={{base: '5', lg: '0' }}>
          <Flex flexDirection={{ base: 'column', lg: 'row' }}>
            {/* Sol sticky başlık */}
            <Box
              maxW={{ base: 'full', lg: '266px' }}
              width="full"
              mb={{ base: '5', lg: '0' }}
            >
              <Text
                color="black.primary3"
                fontSize="lg"
                fontWeight="medium"
                position={{ base: 'relative', lg: 'sticky' }}
                top="25%"
              >
                {sectionData[activeTab].stickyTitle}
              </Text>
            </Box>

            {/* Sağ içerik */}
            <Box
              flex={1}
              maxW="full"
              pl={{ base: '0', lg: '14' }}
              sx={{ '&>*+*': { mt: '4' } }}
            >
              {sectionData[activeTab].staticDescriptions && (
                <ShowMore isOnlyMobileWork={false} startingHeight={410}>
                  <Box fontWeight="light" fontSize="sm" color="black.primary3">
                    <Markup content={sectionData[activeTab].staticDescriptions} />
                  </Box>
                </ShowMore>
              )}

              {sectionData[activeTab].artists?.length > 0 && (
                <Box my="8">
                  <Text color="black.primary3" mb="2" fontWeight="medium">
                    {t('common:artists')}
                  </Text>
                  <Flex flexDirection="column">
                    {sectionData[activeTab].artists.map((artist, index) => (
                      <Text
                        key={index}
                        fontWeight="light"
                        fontSize="sm"
                        color="black.primary3"
                      >
                        {artist.fullName}
                      </Text>
                    ))}
                  </Flex>
                </Box>
              )}
            </Box>
          </Flex>


        </Container>

        <Container
          maxW={{ base: 'full', lg: 'container.xl' }}
          mt={{ base: '10', lg: '20' }}
          sx={{ '&>*+*': { mt: '4' } }}
        >
          <Text
            as="h2"
            color="red.primary"
            fontSize={{ base: '2xl', lg: '35px' }}
            mb={{ base: 'padding.primary', lg: 'padding.primary' }}
          >
            {t('webperformance:togetherTitle')}
          </Text>
          <AnimatedQuestionCloud />
        </Container>


        <Container maxW={{ base: 'full', lg: 'container.xl' }}>
          <Text
            as="h2"
            color="red.primary"
            fontSize={{ base: '2xl', lg: '35px' }}
            mt={{ base: '10', lg: '20' }}
            mb={{ base: 'padding.primary', lg: 'padding.primary' }}
>
            {t('webperformance:projectTeamHeading')}
          </Text>
        </Container>


        <Container maxW={{ base: 'full', lg: 'container.secondary' }}>


          <Flex flexDirection={{ base: 'column', lg: 'row' }} gap="8">
            {/* Sol kolon: roller */}
            <Box flex="1" display="flex" flexDirection="column" gap="4">
              {teamData.map((member, index) => (
                <Text display={{base:"none", lg:"block"}} key={index} fontWeight="medium" fontSize="md" color="black.primary3">
                  {member.role}
                </Text>
              ))}
            </Box>

            {/* Sağ kolon: isimler */}
            <Box flex="1" display="flex" flexDirection="column" gap="4">
              {teamData.map((member, index) => (
                <Box key={index} display="flex" flexDirection="column">
                  {member.names.map((person, i) => (
                    <Text key={i} fontWeight="600" fontSize="md" color="black.primary3">
                      {person.name}, <Text as="span"  fontWeight="light" >{person.title}</Text>
                    </Text>
                  ))}
                </Box>
              ))}
            </Box>
          </Flex>
        </Container>
      </Box>
    </Layout>
  )
}

export default SuzanneLacyBirliktePage

export async function getStaticProps() {
  const pageData = {
    data: {
      hero: {
        subTitle: 'Web Performance',
        title: 'Suzanne Lacy: Birlikte/Togæther',
      },
    },
    error: null,
    slug: 'webperformance/suzannelacy-birlikte',
  }

  return {
    props: { pageData },
    revalidate: 300,
  }
}
